import { createBrowserRouter } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import { protectedRouteLoader } from '@/features/session/routing/protected-route-loader'
import { RouteErrorBoundary } from './RouteErrorBoundary'

export const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <RouteErrorBoundary />,
    lazy: async () => {
      const { LandingPage } =
        await import('@/features/landing/pages/LandingPage')
      return { Component: LandingPage }
    },
  },
  {
    path: '/profile',
    loader: protectedRouteLoader,
    errorElement: <RouteErrorBoundary />,
    lazy: async () => {
      const { AccountLayout } =
        await import('@/features/account/components/AccountLayout')
      return { Component: AccountLayout }
    },
    children: [
      {
        index: true,
        lazy: async () => {
          const { ProfilePage } =
            await import('@/features/account/pages/ProfilePage')
          return { Component: ProfilePage }
        },
      },
      {
        path: 'support',
        lazy: async () => {
          const { SupportPage } =
            await import('@/features/account/pages/SupportPage')
          return { Component: SupportPage }
        },
      },
      {
        path: 'support/ticket',
        lazy: async () => {
          const { SupportTicketPage } =
            await import('@/features/account/pages/SupportTicketPage')
          return { Component: SupportTicketPage }
        },
      },
    ],
  },
  {
    element: <AppLayout />,
    loader: protectedRouteLoader,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: 'dashboard',
        lazy: async () => {
          const { DashboardPage } =
            await import('@/features/dashboard/pages/DashboardPage')
          return { Component: DashboardPage }
        },
      },
      {
        path: 'onboard',
        lazy: async () => {
          const { OnboardingCatalogPage } =
            await import('@/features/onboarding/pages/OnboardingCatalogPage')
          return { Component: OnboardingCatalogPage }
        },
      },
      {
        path: 'onboard/:applicationRecordId',
        lazy: async () => {
          const { ApplicationDetailsPage } =
            await import('@/features/onboarding/pages/ApplicationDetailsPage')
          return { Component: ApplicationDetailsPage }
        },
      },
      {
        path: 'spec-library',
        lazy: async () => {
          const { SpecLibraryPage } =
            await import('@/features/api-design/pages/SpecLibraryPage')
          return { Component: SpecLibraryPage }
        },
      },
      {
        path: 'migration',
        lazy: async () => {
          const { default: MigrationLandingPage } =
            await import('@/features/migration/pages/MigrationLandingPage')
          return { Component: MigrationLandingPage }
        },
      },
      {
        path: 'migration/proxy',
        lazy: async () => {
          const { default: ProxyMigrationWizard } =
            await import('@/features/migration/components/proxy/ProxyMigrationWizard')
          return { Component: ProxyMigrationWizard }
        },
      },
      {
        path: 'migration/:type',
        lazy: async () => {
          const { default: MigrationWizardPage } =
            await import('@/features/migration/pages/MigrationWizardPage')
          return { Component: MigrationWizardPage }
        },
      },
      {
        path: 'config/scm/add',
        lazy: async () => {
          const { default: AddScmConnectionPage } =
            await import('@/features/migration/pages/AddScmConnectionPage')
          return { Component: AddScmConnectionPage }
        },
      },
      {
        path: 'config/kong/:action?',
        lazy: async () => {
          const { KongProfilesPage } = await import('@/features/kong-config')
          return { Component: KongProfilesPage }
        },
      },
      {
        path: 'config/apigee/:action?',
        lazy: async () => {
          const { ApigeeProfilesPage } =
            await import('@/features/apigee-profiles/pages/ApigeeProfilesPage')
          return { Component: ApigeeProfilesPage }
        },
      },
      {
        path: 'management/workflows',
        lazy: async () => {
          const { WorkflowManagementPage } =
            await import('@/features/management/pages/WorkflowManagementPage')
          return { Component: WorkflowManagementPage }
        },
      },
      {
        path: 'management/workflows/new',
        lazy: async () => {
          const { WorkflowEditorPage } =
            await import('@/features/management/pages/WorkflowEditorPage')
          return { Component: WorkflowEditorPage }
        },
      },
      {
        path: 'management/workflows/:workflowId',
        lazy: async () => {
          const { WorkflowDetailPage } =
            await import('@/features/management/pages/WorkflowDetailPage')
          return { Component: WorkflowDetailPage }
        },
      },
      {
        path: 'management/workflows/:workflowId/edit',
        lazy: async () => {
          const { WorkflowEditorPage } =
            await import('@/features/management/pages/WorkflowEditorPage')
          return { Component: WorkflowEditorPage }
        },
      },
      {
        path: 'management/audit-logs',
        lazy: async () => {
          const { AuditLogsPage } =
            await import('@/features/management/pages/AuditLogsPage')
          return { Component: AuditLogsPage }
        },
      },
      {
        path: 'management/cicd',
        lazy: async () => {
          const { CicdAutomationPage } =
            await import('@/features/management/pages/CicdAutomationPage')
          return { Component: CicdAutomationPage }
        },
      },
      {
        path: 'management/approvals',
        lazy: async () => {
          const { ApprovalsPage } =
            await import('@/features/management/pages/ApprovalsPage')
          return { Component: ApprovalsPage }
        },
      },
      {
        path: 'management/approvals/:approvalId',
        lazy: async () => {
          const { ApprovalDetailPage } =
            await import('@/features/management/pages/ApprovalDetailPage')
          return { Component: ApprovalDetailPage }
        },
      },
      {
        path: 'publish/deploy',
        lazy: async () => {
          const { ApiDeployPage } =
            await import('@/features/publish/pages/ApiDeployPage')
          return { Component: ApiDeployPage }
        },
      },
      {
        path: 'publish/deploy/history',
        lazy: async () => {
          const { ApiDeploymentHistoryPage } =
            await import('@/features/publish/pages/ApiDeploymentHistoryPage')
          return { Component: ApiDeploymentHistoryPage }
        },
      },
      {
        path: 'publish/deploy/:resourceId',
        lazy: async () => {
          const { ApiDeploymentDetailPage } =
            await import('@/features/publish/pages/ApiDeploymentDetailPage')
          return { Component: ApiDeploymentDetailPage }
        },
      },
      {
        path: 'publish/test',
        lazy: async () => {
          const { TestWorkspacePage } =
            await import('@/features/publish/pages/TestWorkspacePage')
          return { Component: TestWorkspacePage }
        },
      },
      {
        path: 'publish/test/history',
        lazy: async () => {
          const { TestWorkspacePage } =
            await import('@/features/publish/pages/TestWorkspacePage')
          return { Component: TestWorkspacePage }
        },
      },
      {
        path: 'publish/test/advanced',
        lazy: async () => {
          const { TestWorkspacePage } =
            await import('@/features/publish/pages/TestWorkspacePage')
          return { Component: TestWorkspacePage }
        },
      },
      {
        path: 'publish/governance',
        lazy: async () => {
          const { GovernancePage } =
            await import('@/features/publish/pages/GovernancePage')
          return { Component: GovernancePage }
        },
      },
      {
        path: 'publish/monitoring',
        lazy: async () => {
          const { MonitoringPage } =
            await import('@/features/publish/pages/MonitoringPage')
          return { Component: MonitoringPage }
        },
      },
      {
        path: 'resources/microservice',
        lazy: async () => {
          const { MicroserviceCatalogPage } =
            await import('@/features/lifecycle/resources/microservice/CatalogPage')
          return { Component: MicroserviceCatalogPage }
        },
      },
      {
        path: 'resources/microservice/:stepId',
        lazy: async () => {
          const { MicroserviceWorkflowPage } =
            await import('@/features/lifecycle/resources/microservice/WorkflowPage')
          return { Component: MicroserviceWorkflowPage }
        },
      },
      {
        path: 'resources/microservice/:resourceId/view',
        lazy: async () => {
          const { MicroserviceViewPage } =
            await import('@/features/lifecycle/resources/microservice/ViewPage')
          return { Component: MicroserviceViewPage }
        },
      },
      {
        path: 'resources/microservice/:resourceId/clone',
        lazy: async () => {
          const { MicroserviceClonePage } =
            await import('@/features/lifecycle/resources/microservice/cloning')
          return { Component: MicroserviceClonePage }
        },
      },
      {
        path: 'resources/microservice/:resourceId/version',
        lazy: async () => {
          const { MicroserviceVersionPage } =
            await import('@/features/lifecycle/resources/microservice/versioning')
          return { Component: MicroserviceVersionPage }
        },
      },
      {
        path: 'resources/microservice/:resourceId/deployments',
        lazy: async () => {
          const { MicroserviceDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/microservice/deployment-history')
          return { Component: MicroserviceDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/microservice/:resourceId/:stepId',
        lazy: async () => {
          const { MicroserviceWorkflowPage } =
            await import('@/features/lifecycle/resources/microservice/WorkflowPage')
          return { Component: MicroserviceWorkflowPage }
        },
      },
      {
        path: 'resources/apigee-proxy',
        lazy: async () => {
          const { ApigeeProxyCatalogPage } =
            await import('@/features/lifecycle/resources/apigee-proxy/CatalogPage')
          return { Component: ApigeeProxyCatalogPage }
        },
      },
      {
        path: 'resources/apigee-proxy/:stepId',
        lazy: async () => {
          const { ApigeeProxyWorkflowPage } =
            await import('@/features/lifecycle/resources/apigee-proxy/WorkflowPage')
          return { Component: ApigeeProxyWorkflowPage }
        },
      },
      {
        path: 'resources/apigee-proxy/:resourceId/view',
        lazy: async () => {
          const { ApigeeProxyViewPage } =
            await import('@/features/lifecycle/resources/apigee-proxy/ViewPage')
          return { Component: ApigeeProxyViewPage }
        },
      },
      {
        path: 'resources/apigee-proxy/:resourceId/clone',
        lazy: async () => {
          const { ApigeeProxyClonePage } =
            await import('@/features/lifecycle/resources/apigee-proxy/cloning')
          return { Component: ApigeeProxyClonePage }
        },
      },
      {
        path: 'resources/apigee-proxy/:resourceId/version',
        lazy: async () => {
          const { ApigeeProxyVersionPage } =
            await import('@/features/lifecycle/resources/apigee-proxy/versioning')
          return { Component: ApigeeProxyVersionPage }
        },
      },
      {
        path: 'resources/apigee-proxy/:resourceId/deployments',
        lazy: async () => {
          const { ApigeeProxyDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/apigee-proxy/deployment-history')
          return { Component: ApigeeProxyDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/apigee-proxy/:resourceId/editor',
        lazy: async () => {
          const { ApigeeProxyBundleEditorPage } =
            await import('@/features/apigee-bundle-editor')
          return { Component: ApigeeProxyBundleEditorPage }
        },
      },
      {
        path: 'resources/apigee-proxy/:resourceId/:stepId',
        lazy: async () => {
          const { ApigeeProxyWorkflowPage } =
            await import('@/features/lifecycle/resources/apigee-proxy/WorkflowPage')
          return { Component: ApigeeProxyWorkflowPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow',
        lazy: async () => {
          const { ApigeeSharedFlowCatalogPage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/CatalogPage')
          return { Component: ApigeeSharedFlowCatalogPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:stepId',
        lazy: async () => {
          const { ApigeeSharedFlowWorkflowPage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/WorkflowPage')
          return { Component: ApigeeSharedFlowWorkflowPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:resourceId/view',
        lazy: async () => {
          const { ApigeeSharedFlowViewPage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/ViewPage')
          return { Component: ApigeeSharedFlowViewPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:resourceId/clone',
        lazy: async () => {
          const { ApigeeSharedFlowClonePage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/cloning')
          return { Component: ApigeeSharedFlowClonePage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:resourceId/version',
        lazy: async () => {
          const { ApigeeSharedFlowVersionPage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/versioning')
          return { Component: ApigeeSharedFlowVersionPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:resourceId/deployments',
        lazy: async () => {
          const { ApigeeSharedFlowDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/deployment-history')
          return { Component: ApigeeSharedFlowDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:resourceId/editor',
        lazy: async () => {
          const { ApigeeSharedFlowBundleEditorPage } =
            await import('@/features/apigee-bundle-editor')
          return { Component: ApigeeSharedFlowBundleEditorPage }
        },
      },
      {
        path: 'resources/apigee-shared-flow/:resourceId/:stepId',
        lazy: async () => {
          const { ApigeeSharedFlowWorkflowPage } =
            await import('@/features/lifecycle/resources/apigee-shared-flow/WorkflowPage')
          return { Component: ApigeeSharedFlowWorkflowPage }
        },
      },
      {
        path: 'resources/apigee-config',
        lazy: async () => {
          const { ApigeeConfigPage } = await import('@/features/apigee-config')
          return { Component: ApigeeConfigPage }
        },
      },
      {
        path: 'resources/apigee-config/:resourceKind/:resourceId',
        lazy: async () => {
          const { ApigeeConfigPage } = await import('@/features/apigee-config')
          return { Component: ApigeeConfigPage }
        },
      },
      {
        path: 'resources/kong-gateway-service',
        lazy: async () => {
          const { KongGatewayServiceCatalogPage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/CatalogPage')
          return { Component: KongGatewayServiceCatalogPage }
        },
      },
      {
        path: 'resources/kong-gateway-service/:stepId',
        lazy: async () => {
          const { KongGatewayServiceWorkflowPage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/WorkflowPage')
          return { Component: KongGatewayServiceWorkflowPage }
        },
      },
      {
        path: 'resources/kong-gateway-builder',
        lazy: async () => {
          const { KongConfigPage } = await import('@/features/kong-config')
          return { Component: KongConfigPage }
        },
      },
      {
        path: 'resources/kong-gateway-builder/:resourceKind/:resourceId',
        lazy: async () => {
          const { KongConfigPage } = await import('@/features/kong-config')
          return { Component: KongConfigPage }
        },
      },
      {
        path: 'resources/kong-gateway-service/:resourceId/view',
        lazy: async () => {
          const { KongGatewayServiceViewPage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/ViewPage')
          return { Component: KongGatewayServiceViewPage }
        },
      },
      {
        path: 'resources/kong-gateway-service/:resourceId/clone',
        lazy: async () => {
          const { KongGatewayClonePage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/cloning')
          return { Component: KongGatewayClonePage }
        },
      },
      {
        path: 'resources/kong-gateway-service/:resourceId/version',
        lazy: async () => {
          const { KongGatewayVersionPage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/versioning')
          return { Component: KongGatewayVersionPage }
        },
      },
      {
        path: 'resources/kong-gateway-service/:resourceId/deployments',
        lazy: async () => {
          const { KongGatewayDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/deployment-history')
          return { Component: KongGatewayDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/kong-gateway-service/:resourceId/:stepId',
        lazy: async () => {
          const { KongGatewayServiceWorkflowPage } =
            await import('@/features/lifecycle/resources/kong-gateway-service/WorkflowPage')
          return { Component: KongGatewayServiceWorkflowPage }
        },
      },
      {
        path: 'resources/forgesphere-proxy',
        lazy: async () => {
          const { ForgeSphereProxyCatalogPage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/CatalogPage')
          return { Component: ForgeSphereProxyCatalogPage }
        },
      },
      {
        path: 'resources/forgesphere-proxy/:stepId',
        lazy: async () => {
          const { ForgeSphereProxyWorkflowPage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/WorkflowPage')
          return { Component: ForgeSphereProxyWorkflowPage }
        },
      },
      {
        path: 'resources/forgesphere-config',
        lazy: async () => {
          const { ForgeSphereConfigPage } =
            await import('@/features/forgesphere-config')
          return { Component: ForgeSphereConfigPage }
        },
      },
      {
        path: 'resources/forgesphere-config/:resourceKind/:resourceId',
        lazy: async () => {
          const { ForgeSphereConfigPage } =
            await import('@/features/forgesphere-config')
          return { Component: ForgeSphereConfigPage }
        },
      },
      {
        path: 'resources/forgesphere-proxy/:resourceId/view',
        lazy: async () => {
          const { ForgeSphereGatewayViewPage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/ViewPage')
          return { Component: ForgeSphereGatewayViewPage }
        },
      },
      {
        path: 'resources/forgesphere-proxy/:resourceId/clone',
        lazy: async () => {
          const { ForgeSphereGatewayClonePage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/cloning')
          return { Component: ForgeSphereGatewayClonePage }
        },
      },
      {
        path: 'resources/forgesphere-proxy/:resourceId/version',
        lazy: async () => {
          const { ForgeSphereGatewayVersionPage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/versioning')
          return { Component: ForgeSphereGatewayVersionPage }
        },
      },
      {
        path: 'resources/forgesphere-proxy/:resourceId/deployments',
        lazy: async () => {
          const { ForgeSphereGatewayDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/deployment-history')
          return { Component: ForgeSphereGatewayDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/forgesphere-proxy/:resourceId/:stepId',
        lazy: async () => {
          const { ForgeSphereProxyWorkflowPage } =
            await import('@/features/lifecycle/resources/forgesphere-proxy/WorkflowPage')
          return { Component: ForgeSphereProxyWorkflowPage }
        },
      },
      {
        path: 'resources/forgesphere-function',
        lazy: async () => {
          const { ForgeSphereFunctionCatalogPage } =
            await import('@/features/lifecycle/resources/forgesphere-function/CatalogPage')
          return { Component: ForgeSphereFunctionCatalogPage }
        },
      },
      {
        path: 'resources/forgesphere-function/:stepId',
        lazy: async () => {
          const { ForgeSphereFunctionWorkflowPage } =
            await import('@/features/lifecycle/resources/forgesphere-function/WorkflowPage')
          return { Component: ForgeSphereFunctionWorkflowPage }
        },
      },
      {
        path: 'resources/forgesphere-function/:resourceId/view',
        lazy: async () => {
          const { ForgeSphereFunctionViewPage } =
            await import('@/features/lifecycle/resources/forgesphere-function/ViewPage')
          return { Component: ForgeSphereFunctionViewPage }
        },
      },
      {
        path: 'resources/forgesphere-function/:resourceId/clone',
        lazy: async () => {
          const { ForgeSphereFunctionClonePage } =
            await import('@/features/lifecycle/resources/forgesphere-function/cloning')
          return { Component: ForgeSphereFunctionClonePage }
        },
      },
      {
        path: 'resources/forgesphere-function/:resourceId/version',
        lazy: async () => {
          const { ForgeSphereFunctionVersionPage } =
            await import('@/features/lifecycle/resources/forgesphere-function/versioning')
          return { Component: ForgeSphereFunctionVersionPage }
        },
      },
      {
        path: 'resources/forgesphere-function/:resourceId/deployments',
        lazy: async () => {
          const { ForgeSphereFunctionDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/forgesphere-function/deployment-history')
          return { Component: ForgeSphereFunctionDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/forgesphere-function/:resourceId/:stepId',
        lazy: async () => {
          const { ForgeSphereFunctionWorkflowPage } =
            await import('@/features/lifecycle/resources/forgesphere-function/WorkflowPage')
          return { Component: ForgeSphereFunctionWorkflowPage }
        },
      },
      {
        path: 'resources/mcp',
        lazy: async () => {
          const { McpCatalogPage } =
            await import('@/features/lifecycle/resources/mcp/CatalogPage')
          return { Component: McpCatalogPage }
        },
      },
      {
        path: 'resources/mcp/:stepId',
        lazy: async () => {
          const { McpWorkflowPage } =
            await import('@/features/lifecycle/resources/mcp/WorkflowPage')
          return { Component: McpWorkflowPage }
        },
      },
      {
        path: 'resources/mcp/:resourceId/view',
        lazy: async () => {
          const { McpViewPage } =
            await import('@/features/lifecycle/resources/mcp/ViewPage')
          return { Component: McpViewPage }
        },
      },
      {
        path: 'resources/mcp/:resourceId/clone',
        lazy: async () => {
          const { McpClonePage } =
            await import('@/features/lifecycle/resources/mcp/cloning')
          return { Component: McpClonePage }
        },
      },
      {
        path: 'resources/mcp/:resourceId/version',
        lazy: async () => {
          const { McpVersionPage } =
            await import('@/features/lifecycle/resources/mcp/versioning')
          return { Component: McpVersionPage }
        },
      },
      {
        path: 'resources/mcp/:resourceId/deployments',
        lazy: async () => {
          const { McpDeploymentHistoryPage } =
            await import('@/features/lifecycle/resources/mcp/deployment-history')
          return { Component: McpDeploymentHistoryPage }
        },
      },
      {
        path: 'resources/mcp/:resourceId/:stepId',
        lazy: async () => {
          const { McpWorkflowPage } =
            await import('@/features/lifecycle/resources/mcp/WorkflowPage')
          return { Component: McpWorkflowPage }
        },
      },
    ],
  },
  {
    path: '*',
    loader: protectedRouteLoader,
    errorElement: <RouteErrorBoundary />,
    lazy: async () => {
      const { NotFoundPage } = await import('@/features/not-found/NotFoundPage')
      return { Component: NotFoundPage }
    },
  },
])
